/**
 * Creating a tender from Word: the same flow as the web app's creator.
 *
 * 1. `create_new_bid` with the headline details and `creation_flow: "agent"`.
 * 2. The tender pack goes to the bid's tender library (what the agent's
 *    `search_tender_documents` reads).
 * 3. `POST /bids/{id}/setup` in agent mode. The backend waits for the
 *    documents to land, creates the creator thread (stamped `creation: true`
 *    and scoped to this document, so it sits in the document's history),
 *    copies the pack into its sandbox and starts the first run. Nothing here
 *    depends on the pane staying open.
 * 4. The pane finds the thread once it exists and opens it.
 * 5. `complete_creation` when the completion card's button is pressed.
 */
import { Client } from "@langchain/langgraph-sdk";

import { buildAgentClientOptions } from "@/agents-v2/chat-runtime/agent-client";

import axios from "../helper/axios";
import { apiURL } from "../helper/urls";

export const BID_AGENT_ASSISTANT_ID = "bid_agent";

export type TenderDetailsInput = {
  title: string;
  client: string;
  /** YYYY-MM-DD; the agent asks when it is missing. */
  deadline: string;
  /** Whole pounds as typed, or "". */
  value: string;
};

const auth = (token: string) => ({ Authorization: `Bearer ${token}` });

export const createBid = async (details: TenderDetailsInput, token: string): Promise<string> => {
  const form = new FormData();
  form.append("bid_title", details.title.trim());
  form.append("client_name", details.client.trim());
  form.append("value", details.value.trim());
  form.append("currency", "GBP");
  form.append("submission_deadline", details.deadline);
  form.append("tender_client_profile_website", "");
  form.append("contributors", "{}");
  form.append("creation_flow", "agent");
  const { data } = await axios.post(apiURL("create_new_bid"), form, { headers: auth(token) });
  const bidId = String(data?.bid_id ?? "").trim();
  if (!bidId) throw new Error("The tender was not created.");
  return bidId;
};

const MODE_BY_EXTENSION: Record<string, string> = {
  pdf: "pdf",
  doc: "word",
  docx: "word",
  xls: "excel",
  xlsx: "excel",
  xlsm: "excel",
  ppt: "powerpoint",
  pptx: "powerpoint",
  txt: "plain",
  md: "plain",
};

export const ACCEPTED_TENDER_FILES = ".pdf,.doc,.docx,.xls,.xlsx,.xlsm,.ppt,.pptx,.txt,.md";

const modeFor = (file: File): string => MODE_BY_EXTENSION[(file.name.split(".").pop() ?? "").toLowerCase()] ?? "pdf";

export type UploadProgress = { done: number; total: number; current: string | null };

/**
 * Upload the tender pack to the bid's tender library, one file at a time.
 * @returns The files that failed, with the reason.
 */
export const uploadTenderLibraryFiles = async (
  bidId: string,
  files: readonly File[],
  token: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<{ failed: { name: string; reason: string }[] }> => {
  const failed: { name: string; reason: string }[] = [];
  let done = 0;
  for (const file of files) {
    onProgress?.({ done, total: files.length, current: file.name });
    const form = new FormData();
    form.append("file", file);
    form.append("mode", modeFor(file));
    form.append("bid_id", bidId);
    form.append("on_conflict", "version");
    try {
      await axios.post(apiURL("uploadfile_tenderlibrary"), form, { headers: auth(token), timeout: 120_000 });
    } catch (error) {
      failed.push({ name: file.name, reason: error instanceof Error ? error.message : "Upload failed" });
    }
    done += 1;
  }
  onProgress?.({ done, total: files.length, current: null });
  return { failed };
};

const makeClient = (apiUrl: string, token: string): Client => new Client(buildAgentClientOptions(apiUrl, token));

export type CreationThreadScope = { documentId: string | null; ownerEmail: string | null };

/**
 * Hand the bid's setup to the backend: it starts the creator run once the
 * uploaded documents have landed, on a thread scoped to this document.
 */
export const startSetup = async (input: {
  token: string;
  bidId: string;
  fileNames: readonly string[];
  scope: CreationThreadScope;
}): Promise<void> => {
  await axios.post(
    apiURL(`bids/${encodeURIComponent(input.bidId)}/setup`),
    {
      mode: "agent",
      file_names: [...input.fileNames],
      datasets: [],
      thread_metadata: {
        surface: "word",
        ...(input.scope.documentId && { document_id: input.scope.documentId }),
        ...(input.scope.ownerEmail && { owner_email: input.scope.ownerEmail }),
      },
    },
    { headers: auth(input.token) }
  );
};

/**
 * The creator thread of a bid, when one exists (setup started here or in the
 * web app), so the pane can return to it.
 */
export const findCreationThread = async (apiUrl: string, token: string, bidId: string): Promise<string | null> => {
  const client = makeClient(apiUrl, token);
  const threads = await client.threads.search({
    metadata: { graph_id: BID_AGENT_ASSISTANT_ID, bid_id: bidId, creation: true },
    limit: 1,
    sortBy: "updated_at",
    sortOrder: "desc",
  });
  return threads[0]?.thread_id ?? null;
};

/** Mark the bid's setup finished (the completion card's button). */
export const completeCreation = async (bidId: string, token: string): Promise<void> => {
  await axios.post(apiURL(`bids/${encodeURIComponent(bidId)}/complete_creation`), {}, { headers: auth(token) });
};

// A creation that got as far as a bid but not a running session is resumed
// from here rather than creating a second bid.
const pendingKey = (documentId: string) => `mytender_creation_pending:${documentId}`;
export type PendingCreation = { bidId: string; title: string };

export const readPendingCreation = (documentId: string | null): PendingCreation | null => {
  if (!documentId) return null;
  try {
    const raw = localStorage.getItem(pendingKey(documentId));
    return raw ? (JSON.parse(raw) as PendingCreation) : null;
  } catch {
    return null;
  }
};
export const writePendingCreation = (documentId: string | null, pending: PendingCreation | null): void => {
  if (!documentId) return;
  try {
    if (pending) localStorage.setItem(pendingKey(documentId), JSON.stringify(pending));
    else localStorage.removeItem(pendingKey(documentId));
  } catch {
    // best effort
  }
};
